import { Component, OnInit } from '@angular/core';

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { ConfigOrdemServicoGeralService } from '../../../../shared/services/apis/servico/config-ordem-servico-geral.service';
import { ConfigOrdemServicoGeralRequest } from '../../../../shared/services/requests/servico/config-ordem-servico-geral.request';
import { TipoNumeracaoDocumento } from '../../../../shared/services/models/servico/tipo-numeracao-documento.model';
import { ConfigOrdemServicoGeral } from '../../../../shared/services/models/servico/config-ordem-servico-geral.model';
import { GetConfigOrdemServicoGeralResponse } from '../../../../shared/services/responses/servico/get-config-ordem-servico-geral.response';
import { ConfigOrdemServicoGeralLookupsResponse } from '../../../../shared/services/responses/servico/config-ordem-servico-geral-lookups.response';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { TipoNumeracaoDocumentoEnum } from '../../../../shared/enums/servico/tipo-numeracao-documento.enum';

const PRAZO_PADRAO_EXECUCAO_DIAS = 7;
const ROTA_KANBAN_ORDEM_SERVICO = '/configuracoes/servicos/kanban';

@Component({
  selector: 'app-config-geral-ordem-servico',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    AlertCardComponent,
  ],
  templateUrl: './config-geral-ordem-servico.component.html',
  styleUrls: ['./config-geral-ordem-servico.component.css'],
})
export class ConfigGeralOrdemServicoComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Servicos;
  public isLoading = false;
  public isSaving = false;
  public configCarregada = false;
  private idConfigOrdemServicoGeral = 0;
  public opcoesTipoNumeracao: TipoNumeracaoDocumento[] = [];

  public form = new FormGroup({
    idTipoNumeracaoDocumento: new FormControl<number>(
      TipoNumeracaoDocumentoEnum.Sequencial,
      Validators.required
    ),
    prazoPadraoExecucaoDias: new FormControl<number | null>(PRAZO_PADRAO_EXECUCAO_DIAS, [
      Validators.min(0),
      Validators.pattern(/^\d+$/),
    ]),
    isImprimirObservacoesInternasTecnico: new FormControl<boolean>(true),
  });
  private valoresCarregados = JSON.stringify(this.form.getRawValue());

  constructor(
    private configOrdemServicoGeralService: ConfigOrdemServicoGeralService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarDados();
  }

  private carregarDados(): void {
    this.isLoading = true;
    this.configCarregada = false;
    forkJoin({
      config: this.configOrdemServicoGeralService.get().pipe(catchError(() => of(null))),
      lookups: this.configOrdemServicoGeralService.getLookups().pipe(catchError(() => of(null))),
    }).subscribe({
      next: res => {
        this.isLoading = false;
        this.tratarLookups(res.lookups);
        this.tratarConfig(res.config);
      },
      error: () => {
        this.isLoading = false;
        this.exibirErroApi();
      },
    });
  }

  private tratarLookups(lookups: ConfigOrdemServicoGeralLookupsResponse | null): void {
    if (!lookups) return;

    if (lookups.isError) {
      this.alertService.show('Aviso', lookups.errorDescription, AlertType.warning);
      return;
    }

    this.opcoesTipoNumeracao = lookups.listTipoNumeracaoDocumento || [];
  }

  private tratarConfig(config: GetConfigOrdemServicoGeralResponse | null): void {
    if (config?.isError) {
      this.alertService.show('Aviso', config.errorDescription, AlertType.warning);
      return;
    }

    if (!config?.configOrdemServicoGeral) {
      this.exibirErroApi();
      return;
    }

    this.preencherFormulario(config.configOrdemServicoGeral);
    this.configCarregada = true;
  }

  private preencherFormulario(config: ConfigOrdemServicoGeral): void {
    this.idConfigOrdemServicoGeral = config.idConfigOrdemServicoGeral;
    this.form.patchValue({
      idTipoNumeracaoDocumento: config.idTipoNumeracaoDocumento,
      prazoPadraoExecucaoDias: config.prazoPadraoExecucaoDias,
      isImprimirObservacoesInternasTecnico: config.isImprimirObservacoesInternasTecnico,
    });
    this.valoresCarregados = JSON.stringify(this.form.getRawValue());
  }

  private exibirErroApi(): void {
    this.alertService.show(
      'Aviso',
      'Não foi possível concluir a operação no momento. Por favor, aguarde alguns instantes e tente novamente.',
      AlertType.warning
    );
  }

  public get prazoInvalido(): boolean {
    const control = this.form.controls.prazoPadraoExecucaoDias;
    return control.invalid && control.touched;
  }

  public salvar(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.alertService.show('Aviso', 'Preencha os campos corretamente.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    this.configOrdemServicoGeralService
      .update(this.idConfigOrdemServicoGeral, this.montarRequest())
      .subscribe({
        next: response => {
          this.isSaving = false;
          if (response.isError) {
            this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
            return;
          }
          this.alertService.show('Sucesso', 'Configurações salvas com sucesso!', AlertType.success);
          this.voltar();
        },
        error: () => {
          this.isSaving = false;
          this.exibirErroApi();
        },
      });
  }

  private montarRequest(): ConfigOrdemServicoGeralRequest {
    const raw = this.form.getRawValue();
    return {
      idTipoNumeracaoDocumento: raw.idTipoNumeracaoDocumento ?? TipoNumeracaoDocumentoEnum.Sequencial,
      prazoPadraoExecucaoDias: raw.prazoPadraoExecucaoDias ?? null,
      isImprimirObservacoesInternasTecnico: raw.isImprimirObservacoesInternasTecnico ?? true,
    };
  }

  public abrirConfiguracaoKanban(): void {
    if (JSON.stringify(this.form.getRawValue()) === this.valoresCarregados) {
      this.router.navigate([ROTA_KANBAN_ORDEM_SERVICO], { queryParams: { origem: 'gerais' } });
      return;
    }
    this.dialog
      .open(ConfirmModalComponent, {
        maxWidth: '96vw',
        data: {
          title: 'Descartar alterações?',
          description:
            'Tem certeza que deseja ir para o Kanban de ordem de serviço? Suas alterações serão perdidas.',
          buttonConfirm: 'Ir para o Kanban',
          buttonClose: 'Voltar',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result?.confirm)
          this.router.navigate([ROTA_KANBAN_ORDEM_SERVICO], { queryParams: { origem: 'gerais' } });
      });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/servicos']);
  }
}
